import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useParams, useLocation, Redirect } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import PageLayout from '@/components/cms/page-layout'
import LineItemProduct from '@/components/organisms/LineItemProduct/LineItemProduct'
import Seo from '@/components/seo'
import {
  ShowOnDesktop,
  ShowOnMobile,
  SlideOver,
  Button,
  AlertBox,
  Spinner,
  JsonLd,
  formatProduct,
  Headline
} from 'msp-components'
import {
  Region,
  transformRegionToMap,
  useCommerceAPI,
  useEinstein,
  useSearchParams,
  useCategories,
  useNavigation,
  useMultiSite,
  parse as parseSearchParams,
  isServer,
  ALL_SALE_CATEGORY_ID,
  productUrlBuilder,
  useCookie,
  useActiveData,
  useCurrentSite,
  useItemListTracking
} from 'msp-integrations'
import Categories from './partials/Categories'
import EmptySearchResult from './partials/EmptySearchResult'
import Filters, { EXCLUDED_REFINEMENTS } from './partials/Filters'
import FiltersMobileButton from './partials/FiltersMobileButton'
import ListHeader from './partials/ListHeader'
import Pagination from './partials/Pagination'
import SelectedFilters from './partials/SelectedFilters'
import {
  ProductListProps,
  ProductListParams,
  Refinement
} from './ProductList.types'
import { IconButton } from '@/components/molecules/IconButton'
import { MAX_CACHE_AGE } from '@/constants'

// NOTE: You can ignore certain refinements on a template level by updating the below
// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = ['c_isNew']

const updateRefinements = (productSearchResult) => {
  let selectedFiltersQty = 0
  let refinementsQty = 0

  if (productSearchResult) {
    const selectedRefinements = productSearchResult?.selectedRefinements
    Object.keys(selectedRefinements)
      .filter(
        (refinement) =>
          !EXCLUDED_REFINEMENTS.includes(refinement) && refinement !== 'htype'
      )
      .forEach((selectedFilter) => {
        if (selectedFilter === 'cgid') {
          const refinements = productSearchResult?.refinements
          const baseCategory = getBaseCategory(
            refinements.find(
              (refinement) => refinement.attributeId === selectedFilter
            )
          )
          if (
            baseCategory &&
            selectedRefinements[selectedFilter] !== baseCategory.value
          ) {
            selectedFiltersQty++
          }
        } else {
          selectedFiltersQty +=
            selectedRefinements[selectedFilter].split('|').length
        }
      })

    refinementsQty = productSearchResult?.refinements?.filter((refinement) => {
      return (
        !EXCLUDED_REFINEMENTS.includes(refinement.attributeId) &&
        !!refinement.values
      )
    }).length
  }

  return {
    selectedFiltersQty,
    refinementsQty
  }
}

const updateResults = (productSearchResult) => {
  let categoriesResults = []
  let groupedByCategoriesResults = []

  if (productSearchResult?.hits && productSearchResult?.total > 0) {
    // Note: the first product search hit will always contain the grouping information
    const [firstProductHit] = productSearchResult?.hits

    categoriesResults = firstProductHit.c_categories
    groupedByCategoriesResults = firstProductHit.c_groupedByCategories
  }

  return {
    categoriesResults,
    groupedByCategoriesResults
  }
}

export const isCategoryRefinement = (refinement: Refinement) => {
  return refinement?.attributeId === 'cgid'
}

export const getBaseCategory = (refinement: Refinement) => {
  return isCategoryRefinement(refinement)
    ? Object.values(refinement.values).find(
        (value) => value.value !== 'all-sale'
      )
    : undefined
}

export const getSubcategories = (refinement: Refinement) => {
  return getBaseCategory(refinement)?.values || []
}

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const ProductList = ({
  category,
  categories,
  productSearchResult,
  isLoading,
  page
}: ProductListProps) => {
  const { formatMessage } = useIntl()
  const { buildUrl } = useMultiSite()
  const alertBoxRef = useRef<AlertBox>()
  const [alertBoxMessage, setAlertBoxMessage] = useState({
    type: 'error',
    message: '',
    title: '',
    position: 'bottom',
    showBackdrop: false
  })
  const navigate = useNavigation()
  const { getCookieAllowedById } = useCookie()
  const einstein = useEinstein()
  const activeData = useActiveData()
  const params = useParams<ProductListParams>()
  const { total } = productSearchResult || {}
  const updatedRefinements = updateRefinements(productSearchResult)
  const updatedResults = updateResults(productSearchResult)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [selectedFiltersCount, setSelectedFiltersCount] = useState(
    updatedRefinements.selectedFiltersQty
  )
  const [refinementsCount, setRefinementsCount] = useState(
    updatedRefinements.refinementsQty
  )
  const [categoriesResults, setCategoriesResults] = useState<any[]>(
    updatedResults.categoriesResults
  )
  const [groupedByCategoriesResults, setGroupedByCategoriesResults] = useState<
    any[]
  >(updatedResults.groupedByCategoriesResults)

  const isSalesforceAllowed = getCookieAllowedById('s291') // CONSENTS['s291'] = 'salesforce'

  const { registerItem, trackSelectItem } = useItemListTracking({
    listId: category?.id,
    listName: category?.name,
    enabled: !!category && !!productSearchResult
  })

  const regions = transformRegionToMap(page?.content.regions)

  const showNoResults =
    !isLoading && productSearchResult && !productSearchResult?.hits

  const [searchParams, { stringify: stringifySearchParams }] = useSearchParams()

  const onShowAlert = useCallback(
    (alert: {
      type: string
      message?: string
      title: string
      position?: string
      showBackdrop?: boolean
    }) => {
      setAlertBoxMessage({
        ...alert,
        message: alert.message ?? '',
        position: alert.position ?? 'bottom',
        showBackdrop: alert.showBackdrop ?? false
      })
      alertBoxRef?.current?.showAlert()
    },
    []
  )

  useEffect(() => {
    if (category && productSearchResult) {
      einstein.sendViewCategory(category, productSearchResult)
      activeData.sendViewCategory(searchParams, category, productSearchResult)
    }
  }, [category, productSearchResult, isSalesforceAllowed])

  useEffect(() => {
    const { selectedFiltersQty, refinementsQty } =
      updateRefinements(productSearchResult)

    setSelectedFiltersCount(selectedFiltersQty)
    setRefinementsCount(refinementsQty)
  }, [productSearchResult])

  useEffect(() => {
    const { categoriesResults, groupedByCategoriesResults } =
      updateResults(productSearchResult)
    setCategoriesResults(categoriesResults)
    setGroupedByCategoriesResults(groupedByCategoriesResults)
  }, [productSearchResult?.hits])

  let productIndex = 0
  const productIndexes: Record<string, number> = {}
  categoriesResults.forEach((categoryResult) => {
    groupedByCategoriesResults[categoryResult]?.products?.forEach(
      (product: any) => {
        productIndexes[product.id] = productIndex++
      }
    )
  })

  // Stable click handler so memoized <LineItemProduct> tiles are not
  // re-rendered when this list re-renders. `einstein` is a brand-new object on
  // every render, so read the latest values through a ref instead of closing
  // over them (which would make the callback change every render).
  const productClickDataRef = useRef({
    einstein,
    category,
    productSearchResult,
    registerItem,
    trackSelectItem,
    productIndexes
  })
  useEffect(() => {
    productClickDataRef.current = {
      einstein,
      category,
      productSearchResult,
      registerItem,
      trackSelectItem,
      productIndexes
    }
  })
  const handleProductClick = useCallback((product?: any) => {
    const { einstein, category, productSearchResult, trackSelectItem } =
      productClickDataRef.current
    if (product) {
      trackSelectItem(
        product,
        productClickDataRef.current.productIndexes[product.id]
      )
    }
    einstein.sendViewCategory(category, productSearchResult)
  }, [])
  const registerProduct = useCallback(
    (element: HTMLDivElement | null, product: any) => {
      const { registerItem, productIndexes } = productClickDataRef.current
      const index = productIndexes[product?.id]
      if (typeof index === 'number') {
        registerItem(element, { product, index })
      }
    },
    []
  )

  const toggleFilter = (
    value: string,
    attributeId: string,
    selected: boolean,
    allowMultiple: boolean,
    isCategoryFilter?: boolean,
    parentCategory?: string
  ) => {
    const searchParamsCopy = { ...searchParams }

    if (!isCategoryFilter) {
      delete searchParamsCopy.offset
      delete searchParamsCopy.limit
      delete searchParamsCopy.sort

      if (!allowMultiple) {
        delete searchParamsCopy.refine[attributeId]

        if (!selected) {
          searchParamsCopy.refine[attributeId] = value
        }
      } else {
        const attributeValue = searchParamsCopy.refine[attributeId] || []
        let values = Array.isArray(attributeValue)
          ? attributeValue
          : String(attributeValue).split('|')

        if (!selected) {
          values.push(value)
        } else {
          values = values.filter((v) => String(v) !== value)
        }

        searchParamsCopy.refine[attributeId] = values
        if (searchParamsCopy.refine[attributeId].length === 0) {
          delete searchParamsCopy.refine[attributeId]
        }
      }
    }

    const searchParamQuery = stringifySearchParams(searchParamsCopy)

    const categoryId = isCategoryFilter
      ? selected
        ? parentCategory
        : value
      : params.categoryId
    navigate(`/shop/${categoryId}?${searchParamQuery}`)
  }

  const openFilterFlyout = () => {
    setIsFilterOpen(true)
  }

  const closeFilterFlyout = () => {
    setIsFilterOpen(false)
  }

  return (
    <>
      <Seo
        title={
          page.found && page?.meta?.title
            ? page?.meta?.title
            : category?.pageTitle ||
              formatMessage(
                {
                  defaultMessage: `Shop Category {categoryName}`,
                  id: 'msp.product_list.seo.title'
                },
                { categoryName: category?.name || '' }
              )
        }
        description={
          page.found && page?.meta?.description
            ? page?.meta?.description
            : category?.pageDescription ||
              formatMessage(
                {
                  defaultMessage: `You are in the Shop Category {categoryName}`,
                  id: 'msp.product_list.seo.description'
                },
                { categoryName: category?.name || '' }
              )
        }
        keywords={
          page.found && page?.meta?.keywords
            ? page?.meta?.keywords
            : category?.pageKeywords || ''
        }
        canonicals={
          page.found && page?.meta?.canonicals ? page?.meta?.canonicals : []
        }
      />
      {productSearchResult?.hits && (
        <JsonLd
          type='CollectionPage'
          object={Object.entries(groupedByCategoriesResults)?.reduce(
            (arr, f) => {
              f[1].products.map((p) =>
                arr.push(
                  formatProduct({
                    ...p,
                    slugUrl: buildUrl(
                      productUrlBuilder({
                        id: p.productId || p.id,
                        name: p.name || p.productName,
                        primaryCategoryName: p.c_primaryCategoryName,
                        classCategoryName: p.c_classCategoryName,
                        primaryCategoryPath: p.c_primaryCategoryPath,
                        classCategoryPath: p.c_classCategoryPath
                      })
                    ),
                    image: p?.imageGroups?.[0]?.images[0]?.link
                  })
                )
              )
              return arr
            },
            []
          )}
        />
      )}
      <AlertBox
        ref={alertBoxRef}
        type={alertBoxMessage?.type}
        title={alertBoxMessage?.title}
        description={alertBoxMessage?.message}
        autoCloseTime={3500}
        position={alertBoxMessage?.position}
        showBackdrop={alertBoxMessage?.showBackdrop}
      />
      <div className='bg-lightGray flex w-full items-center justify-center'>
        <div className='md:max-w-screen-3xl w-full bg-white sm:px-12 lg:px-24 2xl:px-48'>
          <Region
            className='sm:-mx-12 lg:-mx-24 2xl:-mx-48'
            region={regions.get('main')}
          />
          <ListHeader className='mt-10 px-4 md:mt-16 md:px-0' count={total}>
            {formatMessage({
              defaultMessage: 'Products',
              id: 'msp.product_list.title'
            })}
          </ListHeader>
          {showNoResults ? (
            <EmptySearchResult />
          ) : (
            <>
              <Categories
                categories={categories}
                selectedCategoryId={params?.categoryId || ALL_SALE_CATEGORY_ID}
              />
              <ShowOnDesktop className='mb-5 px-4 md:px-0'>
                <Filters
                  refinements={productSearchResult?.refinements}
                  selectedRefinements={productSearchResult?.selectedRefinements}
                  toggleFilter={toggleFilter}
                />
              </ShowOnDesktop>
              <ShowOnMobile className='mb-5'>
                {refinementsCount > 0 && (
                  <div className='px-4'>
                    <FiltersMobileButton
                      filterCount={selectedFiltersCount}
                      onOpen={openFilterFlyout}
                    />
                  </div>
                )}
                <SlideOver
                  isOpen={isFilterOpen}
                  onOpen={openFilterFlyout}
                  onClose={closeFilterFlyout}
                >
                  <div className='flex h-full w-screen flex-col bg-white p-8'>
                    <div className='mb-4 flex flex-nowrap'>
                      <div className='font-Roboto text-24 tracking-0 flex w-full flex-nowrap items-center'>
                        {formatMessage({
                          defaultMessage: 'Filter products',
                          id: 'msp.product_list.button.mobile_filter_products'
                        })}
                      </div>
                      <div className='flex min-w-min items-center justify-end'>
                        <IconButton
                          iconName='Close'
                          width='20px'
                          height='20px'
                          onClick={closeFilterFlyout}
                        />
                      </div>
                    </div>
                    <div className='hide-scrollbar h-full overflow-y-auto overflow-x-hidden py-4'>
                      <SelectedFilters
                        className='mb-4'
                        refinements={productSearchResult?.refinements}
                        selectedRefinements={
                          productSearchResult?.selectedRefinements
                        }
                        toggleFilter={toggleFilter}
                      />
                      <Filters
                        mobile
                        refinements={productSearchResult?.refinements}
                        selectedRefinements={
                          productSearchResult?.selectedRefinements
                        }
                        toggleFilter={toggleFilter}
                      />
                    </div>
                    <Button
                      className='bottom-0 mt-4 w-full'
                      onClick={closeFilterFlyout}
                    >
                      {`${formatMessage({
                        defaultMessage: 'Apply',
                        id: 'msp.product_list.button.mobile_apply_filters'
                      })} | ${selectedFiltersCount}`}
                    </Button>
                  </div>
                </SlideOver>
              </ShowOnMobile>
              <SelectedFilters
                className='mb-5 px-4 md:px-0'
                refinements={productSearchResult?.refinements}
                selectedRefinements={productSearchResult?.selectedRefinements}
                toggleFilter={toggleFilter}
              />
              {isLoading ? (
                <Spinner fullScreen />
              ) : (
                <div className='mb-10 flex flex-col md:mx-0 md:mb-12'>
                  {categoriesResults.map((categoryResult) => {
                    const catItem = groupedByCategoriesResults[categoryResult]
                    return (
                      catItem?.products.length > 0 && (
                        <div
                          className='mt-10 md:mt-16'
                          key={catItem.categoryId}
                        >
                          <div className='font-Lora text-28 md:text-30 tracking-0 mx-4 mb-5 font-bold capitalize md:mx-0 md:mb-7'>
                            <Headline tag='h3'>
                              {String(catItem.categoryName).toLowerCase()}
                            </Headline>
                          </div>
                          {catItem.products.map((product) => (
                            <LineItemProduct
                              key={product.id}
                              product={product}
                              type='XS'
                              registerProduct={registerProduct}
                              onClick={handleProductClick}
                              onWishlistChange={onShowAlert}
                              handleAddToCart={onShowAlert}
                            />
                          ))}
                        </div>
                      )
                    )
                  })}
                </div>
              )}
              <Pagination
                className='px-4 md:px-0'
                categories={categories}
                selectedCategoryId={params?.categoryId || ALL_SALE_CATEGORY_ID}
              />
            </>
          )}
          <Region region={regions.get('bottomRegion')} />
        </div>
      </div>
    </>
  )
}

const ProductListWrapper = (props: any) => {
  const { res } = useServerContext()
  const api = useCommerceAPI()
  const queryClient = useQueryClient()
  const location = useLocation()
  const { data: currentSite } = useCurrentSite()
  const { buildUrl } = useMultiSite()
  let { categoryId } = useParams() as {
    categoryId: string
  }
  if (!categoryId) {
    return <Redirect to={buildUrl(`/shop/${ALL_SALE_CATEGORY_ID}`)} />
  }

  const { root } = useCategories()
  const categories = {}
  ;(root?.categories || []).forEach((category: any) => {
    if (category.c_showInQuickshop) {
      categories[category.id] = category
    }
  })

  const searchParams = parseSearchParams(location.search, false)

  useEffect(() => {
    if (currentSite?.site?.preferences?.custom?.trackingQueryParams)
      currentSite.site.preferences.custom.trackingQueryParams
        .split(',')
        .forEach((param: string) => {
          delete searchParams[param.trim()]
        })
  }, [currentSite])

  searchParams.limit = 100
  if (!searchParams.refine.includes(`cgid=${categoryId}`) && categoryId) {
    searchParams.refine.push(`cgid=${categoryId}`)
  }
  // For testing removed
  // searchParams.refine.push('orderable_only=true')

  if (isServer) {
    res.set('Cache-Control', `max-age=${MAX_CACHE_AGE}`)
  }

  const categoryParams = { id: categoryId, levels: 0 }
  const { data: category, isLoading: categoryLoading } = useQuery(
    ['category', categoryParams],
    () =>
      api.shopperProducts.getCategory({
        parameters: categoryParams
      })
  )

  const { data: productSearchResult, isLoading: searchIsLoading } = useQuery(
    ['product-search', searchParams],
    () =>
      api.shopperSearch
        .productSearch({
          parameters: searchParams,
          expand: ['availability']
        })
        .then(async (productSearchResult: any) => {
          const [firstProductHit] = productSearchResult?.hits

          if (firstProductHit) {
            const chunks = []
            const chunkSize = 24
            for (
              let i = 0;
              i < productSearchResult?.hits.length;
              i += chunkSize
            ) {
              let chunkedHits = productSearchResult?.hits.slice(
                i,
                i + chunkSize
              )
              chunks.push(chunkedHits.map((hit: any) => hit.productId))
            }

            const batchResponse = await Promise.all(
              chunks.map((chunk: string[]) => {
                return api.shopperProducts.getProducts({
                  parameters: {
                    ids: chunk.join(','),
                    allImages: true,
                    perPricebook: true
                  }
                })
              })
            )

            const products = new Map()
            batchResponse.forEach((response: any) => {
              response.data.forEach((data: any) => {
                products.set(data.id, data)
                queryClient.setQueryData(['product', data.id], data)
              })
            })

            const groupedByCategoriesResults =
              firstProductHit.c_groupedByCategories
            for (const [key, value] of Object.entries(
              groupedByCategoriesResults
            )) {
              let productsByCat = []
              ;(value as any).products.forEach((productId: string) => {
                productsByCat.push(products.get(productId))
              })
              groupedByCategoriesResults[key].products = productsByCat
            }
          }

          return productSearchResult
        }),
    {
      refetchOnMount: 'always',
      notifyOnChangeProps: ['data']
    }
  )

  // Apply disallow list to refinements.
  if (!searchIsLoading && productSearchResult) {
    productSearchResult.refinements = productSearchResult?.refinements?.filter(
      ({ attributeId }) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)
    )
  }

  // The `isomorphic-sdk` returns error objects when they occur, so we
  // need to check the category type and throw if required.
  if (category?.type?.endsWith('category-not-found')) {
    return <Redirect to={buildUrl('/page-not-found')} />
  }

  return (
    <PageLayout
      {...Object.assign(
        {
          ChildPage: ProductList,
          category,
          categories,
          productSearchResult
        },
        {
          ...props,
          isLoading: searchIsLoading || categoryLoading
        }
      )}
    />
  )
}

export default ProductListWrapper
